import React, { useState, useEffect, useRef } from "react";
import { Link } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { useInView } from "framer-motion";
import ContentfulLongText from "../ContentfulLongText";
import "./sliderImage.css";

const ImageSlideNav = ({ onNextSlide, onPrevSlide }) => (
  <div className="image-slide-nav">
    <button
      className="btn--plain image-slide-nav-arrow image-slide-nav-arrow-left"
      onClick={onPrevSlide}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        strokeWidth={1.5}
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.75 19.5L8.25 12l7.5-7.5"
        />
      </svg>
    </button>
    <button
      className="btn--plain image-slide-nav-arrow image-slide-nav-arrow-right"
      onClick={onNextSlide}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        strokeWidth={1.5}
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8.25 4.5l7.5 7.5-7.5 7.5"
        />
      </svg>
    </button>
  </div>
);

export const SlideImage = ({ slide, className = "" }) => {
  const videoId =
    slide.node.videoMediaLink !== null
      ? slide.node.videoMediaLink.match(
          /(?:\/|%2F)([A-Za-z0-9_-]{11})/
        )[1]
      : "";
  const youtubeEmbedLink = `https://www.youtube.com/embed/${videoId}`;

  return (
    <div className={`image-slide ${className}`}>
      <a
        href={slide.node.buttonLink}
        rel={
          slide.node.isNoFollow
            ? "nofollow noopener noreferrer"
            : ""
        }
      >
        {slide.node.videoMediaLink !== null ? (
          <div className="relative iframe-wrap">
            <iframe
              width="100%"
              height="224"
              src={youtubeEmbedLink}
              title={slide.node.portfolioTitle}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
        ) : (
          <GatsbyImage
            image={getImage(slide.node.portfolioImage)}
            alt={`${slide.node.portfolioTitle} thumbnail`}
          />
        )}
      </a>
      <div className="text-center my-40 slider-content">
        <div className="mx-auto max-w-530">
          <ContentfulLongText
            longTextContent={slide.node.description.description}
          />
        </div>
        <p className="mx-auto">
          <Link
            to={slide.node.buttonLink}
            className="underline-med underline-color-highlight text-light uppercase text-secondary hover_white flex-inline align-center justify-center bold"
            target={slide.node.openInNewTab ? "_blank" : "_self"}
            rel="nofollow noopener noreferrer"
          >
            <span className="btn-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4.5v15m7.5-7.5h-15"
                />
              </svg>
            </span>
            {slide.node.buttonLabel}
          </Link>
        </p>
      </div>
    </div>
  );
};

export const SliderImage = ({ slides, className = "", autoPlay = true }) => {
  const initialSlideIndex = slides.findIndex(
    (slide) => slide.node.slideSequence === 1
  );

  const [currentSlide, setCurrentSlide] = useState(initialSlideIndex);
  const [touchPosition, setTouchPosition] = useState(null);

  const nextSlide = () => {
    setCurrentSlide((currentSlide + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (currentSlide - 1 + slides.length) % slides.length
    );
  };

  useEffect(() => {
    let slideInterval;

    if (autoPlay) {
    }

    return () => {
      clearInterval(slideInterval);
    };
  }, [currentSlide, autoPlay]);

  const handleTouchStart = (e) => {
    const touchDown = e.touches[0].clientX;
    setTouchPosition(touchDown);
  };

  const handleTouchMove = (e) => {
    if (!touchPosition) return;

    const currentTouch = e.touches[0].clientX;
    const diff = touchPosition - currentTouch;

    if (diff > 5) {
      nextSlide();
    } else if (diff < -5) {
      prevSlide();
    }

    setTouchPosition(null);
  };

  const mapPositionToClassName = (
    position,
    totalSlides
  ) => {
    const classes = ["image-slide"];
    const positionClasses = [
      "prev-slide",
      "active",
      "next-slide",
    ];
    const positionIndex =
      (position + totalSlides) % totalSlides;

    classes.push(positionClasses[positionIndex]);

    return classes.join(" ");
  };

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <div className="img-slider-section">
      <div
        ref={ref}
        style={{
          opacity: isInView ? 1 : 0,
          transition:
            "all 0.9s cubic-bezier(0.17, 0.55, 0.55, 1) 0.5s",
        }}
        className="image-slider-wrapper"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
      >
        <div className={`image-slider ${className}`}>
          {slides.map((slide, index) => {
            const position = (index - currentSlide + slides.length) % slides.length;

            return (
              <SlideImage
                key={index}
                slide={slide}
                className={mapPositionToClassName(position, slides.length)}
              />
            );
          })}
        </div>
        {slides.length > 1 ? (
          <ImageSlideNav onNextSlide={nextSlide} onPrevSlide={prevSlide} />
        ) : null}
      </div>
    </div>
  );
};